import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Slider,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TabPanel } from "../../../../../../../components/TabPanel";
import { OrientationType } from "../../../../../../../utils/spots";
import { Shape, ZoneMetadata } from "../../RampCanvasV2";
import { SpotPropertiesSection } from "../../SpotPropertiesSection";

type Props = {
  value: number;
  index: number;
  shape: Shape | undefined;
  shapes: Shape[];
  activeShapes: string[];
  contextMenu: { x: number; y: number; shapeId: string } | null;
  setShapes: (shapes: Shape[]) => void;
  setSelectedShapes: (attribute: string, value: any) => void;
  canvasWidth: number;
  previewSpots: Shape[];
  setPreviewSpots: (spots: Shape[]) => void;
};

export const AlgorithmTab: React.FC<Props> = ({
  value,
  index,
  shape,
  shapes,
  activeShapes,
  contextMenu,
  setShapes,
  setSelectedShapes,
  canvasWidth,
  previewSpots,
  setPreviewSpots,
}) => {
  const { t } = useTranslation();

  if (!shape) return null;

  return (
    <TabPanel value={value} index={index}>
      <Stack direction="column" spacing={1}>
        {/* Algorithm-specific fields */}
        {(shape?.tags?.includes("parkable") ||
          shape?.tags?.includes("reference_line")) && (
          <>
            <Typography variant="caption" fontWeight={600} sx={{ mt: 1 }}>{t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.settings')}</Typography>

            {/* Priority field for both parkable areas and reference lines */}
            <Box>
              <Typography variant="caption" gutterBottom>
                {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.priority')}
              </Typography>
              <Slider
                size="small"
                value={shape?.priority ?? 5}
                onChange={(_, value) => setSelectedShapes("priority", value)}
                min={0}
                max={10}
                step={0.1}
                marks={[
                  { value: 0, label: "0" },
                  { value: 5, label: "5" },
                  { value: 10, label: "10" },
                ]}
                valueLabelDisplay="auto"
                sx={{ "& .MuiSlider-markLabel": { fontSize: "0.7rem" } }}
              />
            </Box>

            {/* Orientation field only for reference lines */}
            {shape?.tags?.includes("reference_line") && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" gutterBottom>
                  {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.aircraftOrientation')}
                </Typography>
                <Stack direction="column" spacing={0.5}>
                  <FormControlLabel
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                    control={
                      <Checkbox
                        size="small"
                        checked={(shape?.orientation || []).includes("nose_to_line")}
                        onChange={(e) => {
                          const currentOrientations = shape?.orientation || [];
                          if (e.target.checked) {
                            if (!currentOrientations.includes("nose_to_line")) {
                              setSelectedShapes("orientation", [...currentOrientations, "nose_to_line"]);
                            }
                          } else {
                            setSelectedShapes("orientation", currentOrientations.filter((o) => o !== "nose_to_line"));
                          }
                        }}
                      />
                    }
                    label={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noseToLine')}
                  />
                  <FormControlLabel
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
                    control={
                      <Checkbox
                        size="small"
                        checked={(shape?.orientation || []).includes("tail_to_line")}
                        onChange={(e) => {
                          const currentOrientations = shape?.orientation || [];
                          if (e.target.checked) {
                            if (!currentOrientations.includes("tail_to_line")) {
                              setSelectedShapes("orientation", [...currentOrientations, "tail_to_line"]);
                            }
                          } else {
                            setSelectedShapes("orientation", currentOrientations.filter((o) => o !== "tail_to_line"));
                          }
                        }}
                      />
                    }
                    label={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.tailToLine')}
                  />
                </Stack>
              </Box>
            )}
          </>
        )}

        {/* Spot reference points */}
        {shape?.tags?.includes("spot") && (
          <ReferencePointsTable
            shape={shape}
            setSelectedShapes={setSelectedShapes}
          />
        )}

        {/* Spot properties for temporal parking algorithm */}
        {shape?.type === "Spot" && (
          <SpotPropertiesSection
            shape={shape}
            shapes={shapes}
            activeShapes={activeShapes}
            contextMenu={contextMenu}
            setShapes={setShapes}
            canvasWidth={canvasWidth}
            previewSpots={previewSpots}
            setPreviewSpots={setPreviewSpots}
          />
        )}

        {/* Zone properties for temporal parking algorithm */}
        {shape?.type === "Zone" && (
          <ZonePropertiesSection
            shape={shape}
            shapes={shapes}
            activeShapes={activeShapes}
            contextMenu={contextMenu}
            setShapes={setShapes}
            setSelectedShapes={setSelectedShapes}
          />
        )}
      </Stack>
    </TabPanel>
  );
};

// Reference Points Table component
type ReferencePointsTableProps = {
  shape: Shape;
  setSelectedShapes: (attribute: string, value: any) => void;
};

const ReferencePointsTable: React.FC<ReferencePointsTableProps> = ({
  shape,
  setSelectedShapes,
}) => {
  const { t } = useTranslation();

  const refPointConfig = [
    {
      name: "north" as const,
      label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.north.label'),
      tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.north.tooltip'),
    },
    {
      name: "south" as const,
      label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.south.label'),
      tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.south.tooltip'),
    },
    {
      name: "east" as const,
      label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.east.label'),
      tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.east.tooltip'),
    },
    {
      name: "west" as const,
      label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.west.label'),
      tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.west.tooltip'),
    },
    {
      name: "center" as const,
      label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.center.label'),
      tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.center.tooltip'),
    },
  ];

  const handleOrientationChange = (
    refPointName: "north" | "south" | "east" | "west" | "center",
    orientation: OrientationType,
    checked: boolean
  ) => {
    if (checked) {
      // Single-select: replace all with just this selection
      setSelectedShapes("reference_points", [{
        name: refPointName,
        validOrientations: [orientation],
      }]);
    } else {
      // Unchecking clears all selections
      setSelectedShapes("reference_points", []);
    }
  };

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="caption" fontWeight={600} gutterBottom>
        {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.referencePoints')}
      </Typography>
      <Table size="small" sx={{ mt: 0.5, tableLayout: "fixed", width: "100%" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ py: 0.5, px: 1, width: 80 }}></TableCell>
            <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}>
              {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.nose')}
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}>
              {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.tail')}
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}>
              {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.overhang')}
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}>
              {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.center')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {refPointConfig.map((refPoint) => {
            const refPointData = (shape?.reference_points || []).find((rp) => rp.name === refPoint.name);
            const isNoseChecked = refPointData?.validOrientations.includes("nose_to_line") || false;
            const isTailChecked = refPointData?.validOrientations.includes("tail_to_line") || false;
            const isCenterChecked = refPointData?.validOrientations.includes("center") || false;
            const isOverhangChecked = refPointData?.validOrientations.includes("overhang") || false;

            return (
              <TableRow key={refPoint.name}>
                <TableCell sx={{ py: 0.25, px: 0.5, width: 80 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption">{refPoint.label}</Typography>
                    <Tooltip title={refPoint.tooltip} placement="right">
                      <IconButton size="small" sx={{ p: 0 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell align="center" sx={{ py: 0.25, px: 0.5, width: 60 }}>
                  {refPoint.name !== "center" ? (
                    <Checkbox
                      size="small"
                      checked={isNoseChecked}
                      onChange={(e) => handleOrientationChange(refPoint.name, "nose_to_line", e.target.checked)}
                      sx={{ p: 0 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.25, px: 0.5, width: 60 }}>
                  {refPoint.name !== "center" ? (
                    <Checkbox
                      size="small"
                      checked={isTailChecked}
                      onChange={(e) => handleOrientationChange(refPoint.name, "tail_to_line", e.target.checked)}
                      sx={{ p: 0 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.25, px: 0.5, width: 60 }}>
                  {refPoint.name !== "center" ? (
                    <Checkbox
                      size="small"
                      checked={isOverhangChecked}
                      onChange={(e) => handleOrientationChange(refPoint.name, "overhang", e.target.checked)}
                      sx={{ p: 0 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ py: 0.25, px: 0.5, width: 60 }}>
                  {refPoint.name === "center" ? (
                    <Checkbox
                      size="small"
                      checked={isCenterChecked}
                      onChange={(e) => handleOrientationChange(refPoint.name, "center", e.target.checked)}
                      sx={{ p: 0 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.disabled">
                      {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

// Zone Properties Section component
type ZonePropertiesSectionProps = {
  shape: Shape;
  shapes: Shape[];
  activeShapes: string[];
  contextMenu: { x: number; y: number; shapeId: string } | null;
  setShapes: (shapes: Shape[]) => void;
  setSelectedShapes: (attribute: string, value: any) => void;
};

const ZonePropertiesSection: React.FC<ZonePropertiesSectionProps> = ({
  shape,
  shapes,
  activeShapes,
  contextMenu,
  setShapes,
  setSelectedShapes,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Typography variant="caption" fontWeight={600} gutterBottom>
        {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneProperties')}
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {/* Disabled Toggle */}
        <FormControlLabel
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
          control={
            <Switch
              size="small"
              checked={(shape.metadata as ZoneMetadata)?.disabled || false}
              onChange={(e) => {
                const updatedShapes = shapes.map((s) => {
                  if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                    return {
                      ...s,
                      metadata: {
                        ...s.metadata,
                        disabled: e.target.checked,
                      },
                    };
                  }
                  return s;
                });
                setShapes(updatedShapes);
              }}
            />
          }
          label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption">
                {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneDisabled.label')}
              </Typography>
              <Tooltip title={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneDisabled.tooltip')}>
                <IconButton size="small" sx={{ p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />

        {/* Staging Area Toggle */}
        <FormControlLabel
          sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
          control={
            <Switch
              size="small"
              checked={(shape.metadata as ZoneMetadata)?.is_staging || false}
              onChange={(e) => {
                const updatedShapes = shapes.map((s) => {
                  if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                    return {
                      ...s,
                      metadata: {
                        ...s.metadata,
                        is_staging: e.target.checked,
                        // Reset overflow parking when disabling staging
                        allow_overflow_parking: e.target.checked
                          ? (s.metadata as any)?.allow_overflow_parking
                          : false,
                      },
                    };
                  }
                  return s;
                });
                setShapes(updatedShapes);
              }}
            />
          }
          label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption">
                {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.stagingArea.label')}
              </Typography>
              <Tooltip title={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.stagingArea.tooltip')}>
                <IconButton size="small" sx={{ p: 0 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />

        {/* Allow Overflow Parking Toggle (only visible for staging zones) */}
        {(shape.metadata as ZoneMetadata)?.is_staging && (
          <FormControlLabel
            sx={{ ml: 2, "& .MuiFormControlLabel-label": { fontSize: "0.8rem" } }}
            control={
              <Switch
                size="small"
                checked={(shape.metadata as any)?.allow_overflow_parking || false}
                onChange={(e) => {
                  const updatedShapes = shapes.map((s) => {
                    if (activeShapes.includes(s.id) || s.id === contextMenu?.shapeId) {
                      return {
                        ...s,
                        metadata: {
                          ...s.metadata,
                          allow_overflow_parking: e.target.checked,
                        },
                      };
                    }
                    return s;
                  });
                  setShapes(updatedShapes);
                }}
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption">
                  {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.overflowParking.label')}
                </Typography>
                <Tooltip title={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.overflowParking.tooltip')}>
                  <IconButton size="small" sx={{ p: 0 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 12, color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            }
          />
        )}

        <TextField
          size="small"
          fullWidth
          label={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneName.label')}
          value={shape?.text || ""}
          onChange={(e) => setSelectedShapes("text", e.target.value)}
          placeholder={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneName.placeholder')}
          helperText={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneName.helperText')}
          InputLabelProps={{ sx: { fontSize: "0.85rem" } }}
          inputProps={{ sx: { fontSize: "0.85rem" } }}
          FormHelperTextProps={{ sx: { fontSize: "0.7rem" } }}
        />
        <Box>
          <Typography variant="caption" gutterBottom>
            {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zonePriority')}
          </Typography>
          <Slider
            size="small"
            value={shape?.priority ?? 5}
            onChange={(_, value) => setSelectedShapes("priority", value)}
            min={0}
            max={10}
            step={1}
            marks={[
              { value: 0, label: "0" },
              { value: 5, label: "5" },
              { value: 10, label: "10" },
            ]}
            valueLabelDisplay="auto"
            sx={{ "& .MuiSlider-markLabel": { fontSize: "0.7rem" } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
            {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zonePriorityHelp')}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};
